import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./About.css";
import CarruselNosotros from "./components/CarruselNosotros";
import useInView from "../../hooks/useInView";
import ContenidoVisual from "../services/components/ContenidoVisual";
import FlipCard from "../../components/ui/FlipCard";
import { motion, Variants } from "framer-motion";
import { AboutBlock } from "./types";

const containerVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.4,
      delayChildren: 0.2,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

export default function Empresa() {
  const { t } = useTranslation();
  const [ref, inView] = useInView({ threshold: 0.2 });
  const [flipped, setFlipped] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | number | undefined>(undefined);
  
  const blocks = t("empresa.blocks", { returnObjects: true }) as AboutBlock[];

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current as any);
    if (!inView) {
      setFlipped(false);
      return;
    }

    const flipLoop = (nextFlip: boolean) => {
      setFlipped(nextFlip);

      const delayEntreTarjetas = 700; // ms para girar cada tarjeta
      const duracionGiro = 1800; // duración animación giro
      const tiempoVisibleDorso = 3000; // ms que se queda mostrando el dorso

      const totalTime =
        (blocks.length - 1) * delayEntreTarjetas +
        duracionGiro +
        (nextFlip ? tiempoVisibleDorso : 800);

      timerRef.current = setTimeout(() => flipLoop(!nextFlip), totalTime);
    };

    flipLoop(true);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current as any);
    };
  }, [inView, blocks.length]);

  return (
    <section
      className="empresa-section"
      style={{ margin: "40px 0 30px 0", zIndex: 2 } as React.CSSProperties}
    >
      <CarruselNosotros />
      <h2 className="empresa-section-titulo">{t("banderas.title")}</h2>

      <motion.div
        className="empresa-blocks"
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        {blocks.map((block, idx) => (
          <motion.div key={block.title} variants={itemVariants}>
            <FlipCard
              title={block.title}
              content={block.content}
              flipped={flipped}
              delay={idx * 600}
            />
          </motion.div>
        ))}
      </motion.div>

      <h4 className="empresa-subtitle2">{t("empresa.subtitle2")}</h4>
      <ContenidoVisual />
    </section>
  );
}
